import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import HiddenTipHint from "@/components/HiddenTipHint";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import {
  ChevronRight,
  ChevronLeft,
  MessageSquare,
  CheckCircle2,
  Sparkles,
  Plus,
  X as XIcon,
  Users,
  History,
  ThumbsUp,
  Trash2,
  Settings,
  CalendarOff,
  ClipboardList,
  Eye,
  Lock,
  Unlock,
} from "lucide-react";
import { toast } from "sonner";
import { ASSIGNMENT_OPTIONS, pad, wdLabel, wdColor, PERIOD_STATUS_LABELS } from "@/lib/yasamaru";
import { hoursOf, fmtH, useIsDesktop, nameOf, deriveDayStatus } from "@/lib/shiftHelpers";
import { Stat, SummaryCard, AssignmentChip, RequestTypeBadge, StatusBadge, CheckCard } from "@/components/shifts/Atoms";
import { Stepper } from "@/components/shifts/Stepper";
import { MagicOverlay } from "@/components/shifts/MagicOverlay";
import { MonthCalendar } from "@/components/shifts/MonthCalendar";
import { SwipeWrapper } from "@/components/shifts/SwipeWrapper";
import { SubmissionDetailDialog } from "@/components/shifts/SubmissionDetailDialog";
import { ProxyInputDialog } from "@/components/shifts/ProxyInputDialog";
import { DayByDayDialog } from "@/components/shifts/DayByDayDialog";
import { NewPeriodDialog } from "@/components/shifts/NewPeriodDialog";

// ================== MAIN ==================
export default function ShiftCreation() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [step, setStep] = useState(1);
  const [staffStatusMap, setStaffStatusMap] = useState({});
  const [requests, setRequests] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [removals, setRemovals] = useState([]);
  const [review, setReview] = useState(null);
  const [assignmentOptions, setAssignmentOptions] = useState(ASSIGNMENT_OPTIONS);
  const [selectedDate, setSelectedDate] = useState("");
  const [newPeriodOpen, setNewPeriodOpen] = useState(false);

  const loadPeriods = async () => {
    const r = await api.get("/shift-periods");
    setPeriods(r.data);
    return r.data;
  };

  useEffect(() => {
    loadPeriods().then((data) => {
      if (data[0]) setPeriodId(data[0].id);
    });
    api.get("/assignments").then((r) => {
      const active = r.data.filter((a) => a.is_active).map((a) => a.name);
      if (active.length) setAssignmentOptions(active);
    }).catch(() => {});
  }, []);

  const period = periods.find((p) => p.id === periodId);
  const days = period ? new Date(period.year, period.month, 0).getDate() : 0;

  const loadData = async () => {
    if (!periodId) return;
    const [reqRes, shiftRes, statusRes, rmRes] = await Promise.all([
      api.get(`/shift-requests?shift_period_id=${periodId}`),
      api.get(`/shifts?shift_period_id=${periodId}`),
      api.get(`/shifts/staff-status/${periodId}`),
      api.get(`/shifts/removal-history/${periodId}`),
    ]);
    setRequests(reqRes.data);
    setShifts(shiftRes.data);
    const map = {};
    for (const s of statusRes.data) map[s.user_id] = s;
    setStaffStatusMap(map);
    setRemovals(rmRes.data);
  };
  useEffect(() => { loadData(); }, [periodId]);

  useEffect(() => {
    if (step === 5 && periodId) {
      api.get(`/shifts/period-review/${periodId}`).then((r) => setReview(r.data));
    }
  }, [step, periodId, shifts]);

  // 配置マジック実行済み = いずれかのシフトが存在する
  const magicDone = shifts.length > 0;

  // Click date in calendar opens day panel
  const openDayPanel = (dateStr) => { setSelectedDate(dateStr); if (step < 3 && magicDone) setStep(3); };

  return (
    <div className="space-y-4" data-testid="shift-creation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">やさまるシフト</h1>
          <p className="text-sm text-slate-500">やさしく、まるく、シフトにマジック。</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={periodId} onValueChange={setPeriodId}>
            <SelectTrigger className="w-56 bg-white" data-testid="creation-period-select">
              <SelectValue placeholder={periods.length === 0 ? "対象月がありません" : "対象月を選択"} />
            </SelectTrigger>
            <SelectContent>
              {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.year}年{p.month}月分 ({PERIOD_STATUS_LABELS[p.status] || p.status})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm"
            onClick={() => setNewPeriodOpen(true)}
            data-testid="open-new-period"
            className="bg-white whitespace-nowrap"
            title="新しい対象月を作成"
          >
            <Plus className="w-4 h-4 mr-1" />新規
          </Button>
        </div>
      </div>

      <Stepper step={step} onChange={setStep} magicDone={magicDone} />

      {!periodId ? (
        <Card className="p-8 text-center" data-testid="empty-periods-card">
          <div className="text-5xl mb-3">📅</div>
          <h3 className="font-bold text-slate-900 mb-1">対象月がまだありません</h3>
          <p className="text-sm text-slate-500 mb-5">
            シフトを作成するには、まず「対象月」を作成してください。
          </p>
          <Button
            onClick={() => setNewPeriodOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="empty-create-period"
          >
            <Plus className="w-4 h-4 mr-1" />新しい対象月を作成
          </Button>
        </Card>
      ) : (
        <>
          {(step === 1 || step === 2) && (
            <StepMonth
              viewMode={step === 1 ? "prep" : "magic"}
              period={period} days={days} periodId={periodId}
              shifts={shifts} requests={requests} staffStatusMap={staffStatusMap}
              onDateClick={openDayPanel}
              onReload={loadData}
              onPrev={step === 2 ? () => setStep(1) : null}
              onNext={() => setStep(step + 1)}
            />
          )}
          {step === 3 && (
            <StepAdjustDay
              period={period} days={days} periodId={periodId}
              shifts={shifts} requests={requests} staffStatusMap={staffStatusMap}
              assignmentOptions={assignmentOptions}
              selectedDate={selectedDate} setSelectedDate={setSelectedDate}
              removals={removals}
              onChanged={loadData}
              onShiftAdded={(ns) => setShifts((prev) => [...prev, ns])}
              onPrev={() => setStep(2)} onNext={() => setStep(4)}
            />
          )}
          {step === 4 && (
            <StepStaffReview periodId={periodId} onPrev={() => setStep(3)} onNext={() => setStep(5)} />
          )}
          {step === 5 && (
            <StepPublish
              period={period} periodId={periodId} review={review}
              shifts={shifts} staffStatusMap={staffStatusMap}
              onPublished={async () => { await loadData(); toast.success("シフトを公開しました"); }}
              onPrev={() => setStep(4)}
              onJumpToDay={(d) => { setSelectedDate(d); setStep(3); }}
            />
          )}
        </>
      )}

      <NewPeriodDialog
        open={newPeriodOpen}
        onOpenChange={setNewPeriodOpen}
        existingPeriods={periods}
        onCreated={async (created) => {
          await loadPeriods();
          setPeriodId(created.id);
          setStep(1);
        }}
      />
    </div>
  );
}

// ========== STEP 1: 今月の準備 (提出状況 + 月の準備) / STEP 2: 配置マジック ==========
function StepMonth({ viewMode = "prep", period, days, periodId, shifts, requests, staffStatusMap, onDateClick, onReload, onPrev, onNext }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [aiDoneLocal, setAiDone] = useState(false);
  const aiDone = aiDoneLocal || (shifts && shifts.length > 0);
  const [closedDates, setClosedDates] = useState([]);
  const [weekdayReq, setWeekdayReq] = useState({});
  const [savingSetup, setSavingSetup] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(null);
  const [detailUser, setDetailUser] = useState(null); // submitted-staff detail dialog
  const [proxyTarget, setProxyTarget] = useState(null); // staff for proxy input

  // 各曜日が「全て休業日」かを計算（true なら必要人数=0強制）
  const closedWeekdays = useMemo(() => {
    if (!period) return {};
    const wdCount = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    const wdClosed = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    for (let d = 1; d <= days; d++) {
      const wd = new Date(period.year, period.month - 1, d).getDay();
      wdCount[wd]++;
      const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
      if (closedDates.includes(dateStr)) wdClosed[wd]++;
    }
    const all = {};
    for (let i = 0; i < 7; i++) all[i] = wdCount[i] > 0 && wdClosed[i] === wdCount[i];
    return all;
  }, [period, days, closedDates]);

  useEffect(() => {
    if (period) {
      setClosedDates(period.closed_dates || []);
      const def = { "0": 4, "1": 2, "2": 2, "3": 2, "4": 2, "5": 3, "6": 4 };
      setWeekdayReq({ ...def, ...(period.weekday_required_staff || {}) });
      setPlacementMode(period.placement_mode || "request_first");
    }
  }, [period]);

  const [placementMode, setPlacementMode] = useState("request_first");
  const [magicOpen, setMagicOpen] = useState(false);
  const [magicStage, setMagicStage] = useState(null); // null | 'reading' | 'placing' | 'finalizing'

  const runMagic = async (mode) => {
    setMagicOpen(false);
    setBusy(true);
    setAiDone(false);
    setResult(null);
    // 演出: ステージを進める + 5秒経過で必ずオーバーレイを閉じる (API完了に依らない)
    const stageTimers = [
      setTimeout(() => setMagicStage("reading"), 0),
      setTimeout(() => setMagicStage("placing"), 1700),
      setTimeout(() => setMagicStage("finalizing"), 3500),
    ];
    const startedAt = Date.now();
    // toast は overlay クローズと同時に出す
    let pendingToast = null;
    const hideTimer = setTimeout(() => {
      setMagicStage(null);
      if (pendingToast) { pendingToast(); pendingToast = null; }
    }, 5000);
    try {
      const apiRes = await api.post("/shifts/bulk-assign-requests", {
        shift_period_id: periodId,
        mode, // "fill" | "fresh"
        placement_mode: placementMode, // "request_first" | "strict_required"
      });
      const data = apiRes.data;
      setResult({ ...data, _mode: mode });
      setAiDone(true);
      await onReload();
      const showToast = () => {
        if (mode === "fresh") {
          toast.success(`作り直し完了：${data.cleared || 0}件をクリアし、${data.created}件を新規登録`);
        } else {
          toast.success(`${data.created}件の仮シフトを追加しました`);
        }
      };
      // 5秒経過済みなら即時表示、未満なら hideTimer に予約
      if (Date.now() - startedAt >= 5000) showToast();
      else pendingToast = showToast;
    } catch (err) {
      toast.error(formatApiError(err));
      // エラー時はオーバーレイを即時クローズ
      stageTimers.forEach(clearTimeout);
      clearTimeout(hideTimer);
      pendingToast = null;
      setMagicStage(null);
    } finally {
      setBusy(false);
      const elapsed = Date.now() - startedAt;
      if (elapsed >= 5000) {
        stageTimers.forEach(clearTimeout);
        clearTimeout(hideTimer);
        setMagicStage(null);
        if (pendingToast) { pendingToast(); pendingToast = null; }
      }
      // else: hideTimer (5秒) で overlay 終了と toast の両方を発火
    }
  };

  const saveSetup = async () => {
    setSavingSetup(true);
    try {
      const wr = {};
      for (let i = 0; i < 7; i++) {
        // 全休業の曜日は強制 0
        wr[String(i)] = closedWeekdays[i] ? 0 : Number(weekdayReq[String(i)] ?? 2);
      }
      await api.put(`/shift-periods/${periodId}`, {
        closed_dates: closedDates,
        weekday_required_staff: wr,
        placement_mode: placementMode,
      });
      toast.success("準備を保存しました");
      await onReload();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingSetup(false); }
  };

  const toggleClosed = (dateStr) => {
    setClosedDates((prev) => prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : [...prev, dateStr]);
  };

  // Submission stats
  const submittedByUser = useMemo(() => {
    const m = {};
    for (const r of requests) {
      m[r.user_id] = m[r.user_id] || new Set();
      m[r.user_id].add(r.date);
    }
    return m;
  }, [requests]);

  const staffList = Object.values(staffStatusMap);
  const submitted = staffList.filter((s) => submittedByUser[s.user_id]?.size > 0);
  const notSubmitted = staffList.filter((s) => !(submittedByUser[s.user_id]?.size > 0));
  const workdays = days - (closedDates.length || 0);

  const sendReminder = async (uid) => {
    setReminderBusy(uid);
    try {
      await api.post("/messages", {
        to_user_id: uid,
        message_type: "reminder",
        body: `${period.year}年${period.month}月分のシフト希望提出をお願いします。`,
        shift_period_id: periodId,
      });
      toast.success("リマインドを送信しました");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReminderBusy(null); }
  };

  const sendReminderAll = async () => {
    if (notSubmitted.length === 0) return;
    if (!window.confirm(`未提出${notSubmitted.length}名に一斉にリマインドを送信します。よろしいですか？`)) return;
    try {
      await Promise.all(notSubmitted.map((s) => api.post("/messages", {
        to_user_id: s.user_id,
        message_type: "reminder",
        body: `${period.year}年${period.month}月分のシフト希望提出をお願いします。`,
        shift_period_id: periodId,
      })));
      toast.success(`${notSubmitted.length}名にリマインドを送信しました`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!period) return null;
  const firstWd = new Date(period.year, period.month - 1, 1).getDay();

  return (
    <div className={`space-y-4 ${viewMode === "prep" ? "flex flex-col space-y-0 gap-4" : ""}`} data-testid={viewMode === "prep" ? "step1-prep" : "step2-magic"}>
      {/* === STEP 1: 提出状況 → 月の準備 === */}
      {viewMode === "prep" && (<>
      {/* 月の準備設定 (DOM後だが order:2 で 提出状況の下に来る) */}
      <Card className="p-4 bg-white border border-slate-200" style={{order: 2}}>
        <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
          <Settings className="w-5 h-5 text-blue-600" />月の準備
        </h2>

        {/* ① 休業日 (先に設定) */}
        <section className="mb-5">
          <h4 className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-1">
            <CalendarOff className="w-4 h-4" /><span className="text-blue-600 font-bold mr-1">①</span>休業日 (タップで切替)
          </h4>
          <p className="text-[11px] text-slate-500 mb-2">休業日はシフト追加対象外。全休の曜日は必要人数が自動で 0 になります。選択中: <b>{closedDates.length}日</b></p>
          <div className="grid grid-cols-7 gap-1 max-w-2xl mx-auto">
            {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
              <div key={w} className={`text-center text-xs font-bold py-1 ${i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : "text-slate-600"}`}>{w}</div>
            ))}
            {Array.from({ length: firstWd }, (_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
              const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
              const wd = new Date(period.year, period.month - 1, d).getDay();
              const isClosed = closedDates.includes(dateStr);
              return (
                <button
                  key={dateStr} type="button"
                  data-testid={`closed-${d}`}
                  onClick={() => toggleClosed(dateStr)}
                  className={`text-center text-sm py-1.5 rounded border-2 transition-all ${
                    isClosed ? "bg-rose-100 border-rose-400 text-rose-900 font-bold" :
                    "bg-white border-slate-200 hover:border-blue-300 text-slate-700"
                  }`}
                >
                  <div className={wdColor(wd)}>{d}</div>
                  {isClosed && <div className="text-[9px]">休</div>}
                </button>
              );
            })}
          </div>
        </section>

        {/* ② 曜日別必要人数 */}
        <section className="mb-3">
          <h4 className="text-sm font-bold text-slate-900 mb-1 flex items-center gap-1">
            <Users className="w-4 h-4" /><span className="text-blue-600 font-bold mr-1">②</span>曜日別の必要人数（全体の必要人数を設定してください）
          </h4>
          <p className="text-[11px] text-slate-500 mb-3">※ 役割などの配置は後ほど個別で設定します。</p>
          <div className="grid grid-cols-7 gap-2 max-w-3xl mx-auto">
            {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => {
              const allClosed = closedWeekdays[i];
              const cur = allClosed ? 0 : Number(weekdayReq[String(i)] ?? 2);
              const setVal = (v) => setWeekdayReq({ ...weekdayReq, [String(i)]: Math.max(0, Math.min(20, v)) });
              return (
                <div key={i} className="text-center">
                  <Label className={`text-xs ${i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : "text-slate-700"} block mb-1.5 font-semibold`}>{w}</Label>
                  <div className="flex flex-col items-center gap-1">
                    <button
                      type="button"
                      data-testid={`wd-req-${i}-inc`}
                      onClick={() => setVal(cur + 1)}
                      disabled={allClosed || cur >= 20}
                      aria-label="増やす"
                      className={`w-full h-9 flex items-center justify-center rounded-md border-2 text-lg font-bold transition-colors ${
                        allClosed || cur >= 20
                          ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                          : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-400 active:bg-blue-200"
                      }`}
                    >＋</button>
                    <div
                      data-testid={`wd-req-${i}`}
                      className={`w-full min-h-[3rem] flex items-center justify-center border-2 rounded-md ${
                        allClosed ? "bg-slate-100 border-slate-200" : "border-slate-300 bg-white"
                      }`}
                    >
                      <span className={`text-2xl font-bold select-none ${allClosed ? "text-slate-400" : "text-slate-900"}`}>{cur}</span>
                    </div>
                    <button
                      type="button"
                      data-testid={`wd-req-${i}-dec`}
                      onClick={() => setVal(cur - 1)}
                      disabled={allClosed || cur <= 0}
                      aria-label="減らす"
                      className={`w-full h-9 flex items-center justify-center rounded-md border-2 text-lg font-bold transition-colors ${
                        allClosed || cur <= 0
                          ? "border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-400 active:bg-slate-100"
                      }`}
                    >−</button>
                    <span className={`text-[10px] mt-0.5 ${allClosed ? "text-rose-500 font-semibold" : "text-slate-400"}`}>
                      {allClosed ? "休業" : "人"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ③ 配置モード選択 */}
        <section className="mb-3">
          <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1">
            <Sparkles className="w-4 h-4 text-purple-500" /><span className="text-blue-600 font-bold mr-1">③</span>配置マジックのモード
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
            <button
              type="button"
              onClick={() => setPlacementMode("request_first")}
              data-testid="placement-mode-request-first"
              className={`text-left border-2 rounded-lg p-3 transition-all ${
                placementMode === "request_first" ? "border-purple-500 bg-purple-50" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">🌟</span>
                <b className="text-sm">希望優先</b>
                {placementMode === "request_first" && <span className="text-[10px] bg-purple-600 text-white px-1.5 py-0.5 rounded ml-auto">選択中</span>}
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                ◎「入れます」を出した人を <b>必要人数を超えても</b> 全員配置。<br />
                スタッフの希望を最大限尊重する標準モード。
              </p>
            </button>
            <button
              type="button"
              onClick={() => setPlacementMode("strict_required")}
              data-testid="placement-mode-strict"
              className={`text-left border-2 rounded-lg p-3 transition-all ${
                placementMode === "strict_required" ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-white"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">🎯</span>
                <b className="text-sm">必要人数厳守</b>
                {placementMode === "strict_required" && <span className="text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded ml-auto">選択中</span>}
              </div>
              <p className="text-[11px] text-slate-600 leading-relaxed">
                曜日別の <b>必要人数に達したら追加配置しない</b>。<br />
                人件費を抑えたい時、シフト過多を避けたい時に。
              </p>
            </button>
          </div>
        </section>

        <div className="flex justify-end">
          <Button data-testid="save-setup" onClick={saveSetup} disabled={savingSetup} className="bg-blue-600 hover:bg-blue-700 text-white">
            {savingSetup ? "保存中..." : "準備を保存する"}
          </Button>
        </div>
      </Card>
      </>)}

      {/* 提出状況 */}
      {viewMode === "prep" && (
      <Card className="p-4 bg-white border border-slate-200" style={{order: 1}} data-testid="submission-panel">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />提出状況
          </h2>
          {notSubmitted.length > 0 && (
            <Button size="sm" variant="outline" data-testid="remind-all" onClick={sendReminderAll} className="border-yellow-300 text-yellow-800 hover:bg-yellow-50">
              <MessageSquare className="w-3.5 h-3.5 mr-1" />未提出 {notSubmitted.length}名にまとめてリマインド
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* 提出済み */}
          <div className="border border-emerald-200 rounded">
            <div className="bg-emerald-50 px-3 py-2 border-b border-emerald-200 flex items-center justify-between">
              <p className="font-bold text-emerald-800">提出済み</p>
              <span className="text-2xl font-bold text-emerald-800">{submitted.length}名</span>
            </div>
            {submitted.length === 0 ? (
              <p className="text-xs text-slate-400 p-3 text-center">まだ誰も提出していません</p>
            ) : (
              <ul className="divide-y divide-emerald-100 max-h-96 overflow-y-auto">
                {submitted.map((s) => {
                  const caps = s.capable_assignments || [];
                  return (
                    <li key={s.user_id} className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap hover:bg-emerald-100/50 transition-colors">
                      <button
                        type="button"
                        onClick={() => setDetailUser(s)}
                        data-testid={`submitted-${s.user_id}`}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-medium text-sm text-slate-800 whitespace-nowrap">{s.display_name || s.name}</span>
                          {s.has_late_submission && (
                            <span
                              className="text-[10px] font-bold bg-orange-100 text-orange-800 border border-orange-300 rounded px-1.5 py-0.5"
                              title="締切後の遅れ提出が含まれます"
                              data-testid={`late-badge-${s.user_id}`}
                            >
                              遅れ
                            </span>
                          )}
                          {caps.length > 0 ? (
                            <div className="flex flex-wrap gap-1" data-testid={`caps-${s.user_id}`}>
                              {caps.slice(0, 5).map((a) => (
                                <span key={a} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">{a}</span>
                              ))}
                              {caps.length > 5 && <span className="text-[10px] text-slate-400">+{caps.length - 5}</span>}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400">配置 未設定</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-600">◎{s.requested_days} △{s.either_days} ×{s.unavailable_days}</div>
                      </button>
                      <Button
                        size="sm" variant="outline"
                        className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50 shrink-0"
                        onClick={() => setProxyTarget(s)}
                        data-testid={`edit-${s.user_id}`}
                        title="希望を編集 (上書き)"
                      >
                        <ClipboardList className="w-3 h-3 mr-1" />編集
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* 未提出 */}
          <div className={`border rounded ${notSubmitted.length > 0 ? "border-yellow-200" : "border-slate-200"}`}>
            <div className={`px-3 py-2 border-b flex items-center justify-between ${notSubmitted.length > 0 ? "bg-yellow-50 border-yellow-200" : "bg-slate-50 border-slate-200"}`}>
              <p className={`font-bold ${notSubmitted.length > 0 ? "text-yellow-800" : "text-slate-700"}`}>未提出</p>
              <span className={`text-2xl font-bold ${notSubmitted.length > 0 ? "text-yellow-800" : "text-slate-700"}`}>{notSubmitted.length}名</span>
            </div>
            {notSubmitted.length === 0 ? (
              <p className="text-xs text-emerald-700 p-3 text-center font-semibold">✓ 全員提出済みです</p>
            ) : (
              <ul className="divide-y divide-yellow-100 max-h-96 overflow-y-auto">
                {notSubmitted.map((s) => (
                  <li key={s.user_id} className="px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                    <span className="font-medium text-sm text-slate-800">{s.display_name || s.name}</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 text-xs border-yellow-300 text-yellow-800 hover:bg-yellow-50"
                              disabled={reminderBusy === s.user_id}
                              onClick={() => sendReminder(s.user_id)}
                              data-testid={`remind-${s.user_id}`}>
                        <MessageSquare className="w-3 h-3 mr-1" />リマインド
                      </Button>
                      <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={() => setProxyTarget(s)}
                              data-testid={`proxy-${s.user_id}`}>
                        <ClipboardList className="w-3 h-3 mr-1" />代理入力
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">※ 営業日 {workdays}日 (休業日 {closedDates.length}日 を除く)</p>
      </Card>
      )}

      {/* === 提出済みスタッフ 詳細ダイアログ === */}
      {viewMode === "prep" && (
        <SubmissionDetailDialog
          user={detailUser}
          period={period}
          days={days}
          closedDates={closedDates}
          requests={requests}
          onClose={() => setDetailUser(null)}
        />
      )}

      {/* === 代理入力ダイアログ === */}
      {viewMode === "prep" && (
        <ProxyInputDialog
          target={proxyTarget}
          period={period}
          days={days}
          closedDates={closedDates}
          periodId={periodId}
          existingRequests={requests}
          onClose={() => setProxyTarget(null)}
          onSaved={onReload}
        />
      )}

      {/* === Prep Next === */}
      {viewMode === "prep" && (
        <Card className="p-4 bg-blue-50 border-2 border-blue-200 flex items-center justify-between flex-wrap gap-3" style={{order: 3}}>
          <p className="text-sm text-blue-900">準備ができたら、配置マジックでベスト配置を自動で作りましょう。</p>
          <Button data-testid="goto-step2-magic" onClick={onNext} className="bg-purple-600 hover:bg-purple-700 text-white">
            <Sparkles className="w-4 h-4 mr-1" />次へ：配置マジック <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </Card>
      )}

      {/* === STEP 2: 配置マジック === */}
      {viewMode === "magic" && (<>
      {/* 配置マジック */}
      <Card className={`p-4 bg-white border-2 transition-colors ${aiDone ? "border-emerald-300" : "border-slate-200"}`}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />配置マジック
          </h2>
          <Button data-testid="ai-suggest-button" onClick={() => setMagicOpen(true)} disabled={busy} className="bg-purple-600 hover:bg-purple-700 text-white h-12 px-6">
            <Sparkles className="w-4 h-4 mr-2" />{busy ? "実行中..." : (aiDone ? "もう一度起動する" : "配置マジックを起動する")}
          </Button>
        </div>
        <p className="text-xs text-slate-500">もう一度魔法をかけて配置します！</p>
        {aiDone && (
          <div className="mt-1.5">
            <HiddenTipHint
              text="遅れて希望出した人だけ追加配置するなら「空き埋め」モード"
              anchor="tip-fill"
              subtle
            />
          </div>
        )}
        {aiDone && result && (
          <div className="mt-3 space-y-2" data-testid="ai-done-banner">
            <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-300 rounded">
              <img src="/yasamaru-celebrate-128.png" alt="やさまる" className="w-14 h-14 shrink-0" />
              <div className="flex-1">
                <p className="font-bold text-emerald-900 text-sm">
                  配置マジックが完了しました！ <span className="text-[11px] text-emerald-700 font-normal ml-1">({result._mode === "fresh" ? "作り直し" : "空き埋め"})</span>
                </p>
                <p className="text-xs text-emerald-700 mt-0.5">
                  {result._mode === "fresh" && (<>仮シフト <b>{result.cleared || 0}件</b> をクリアし、 </>)}
                  仮シフト <b>{result.created}件</b> を登録しました。下の「次へ：日を調整する」から、日ごとの最終調整に進めます。
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
              <Stat label="登録" value={result.created} tone="emerald" />
              <Stat label="既存" value={result.skipped_existing} tone="slate" />
              <Stat label="希望達成" value={result.skipped_max} tone="yellow" />
              <Stat label="週上限" value={result.skipped_weekly || 0} tone="orange" />
              <Stat label="連勤回避" value={result.skipped_consecutive || 0} tone="rose" />
            </div>
            <div className="pt-1">
              <HiddenTipHint
                text="◎なのに入っていない人は？ 上限ルールで外されてるかも"
                anchor="tip-limits"
                subtle
              />
            </div>
          </div>
        )}
      </Card>

      {/* 配置マジック モード選択ダイアログ */}
      <Dialog open={magicOpen} onOpenChange={setMagicOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />配置マジックの種類を選ぶ
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <button
              type="button"
              data-testid="magic-mode-fresh"
              onClick={() => runMagic("fresh")}
              className="w-full text-left border-2 border-rose-200 hover:border-rose-400 hover:bg-rose-50 rounded-lg p-3 transition-all"
            >
              <p className="font-bold text-sm text-rose-800">
                <span className="inline-block px-1.5 py-0.5 bg-rose-100 rounded mr-1 text-[10px]">A</span>
                作り直す（締め切り後のシフトを加味して作り直す）
              </p>
              <p className="text-[11px] text-slate-600 mt-1">
                今ある仮シフトを<b>全部クリアして</b>、最新の希望に合わせてゼロから作り直します。締め切り後に追加された希望や日数変更を全部加味したい時にどうぞ。
                <br /><span className="text-rose-700">※ <b>確定</b>マークを付けた人と、公開済みの確定シフトは消えません。</span>
              </p>
            </button>

            <button
              type="button"
              data-testid="magic-mode-fill"
              onClick={() => runMagic("fill")}
              className="w-full text-left border-2 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50 rounded-lg p-3 transition-all"
            >
              <p className="font-bold text-sm text-emerald-800">
                <span className="inline-block px-1.5 py-0.5 bg-emerald-100 rounded mr-1 text-[10px]">B</span>
                空きを埋める（今空いているところをベースに入れ込む）
              </p>
              <p className="text-[11px] text-slate-600 mt-1">
                今ある仮シフトは<b>そのままに</b>、まだ誰も入っていない日だけを埋めます。手動で調整した分を活かしたまま、新しい希望者を追加したい時にどうぞ。
              </p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMagicOpen(false)}>キャンセル</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Magic-step Next */}
      <Card className={`p-4 border-2 flex items-center justify-between flex-wrap gap-3 transition-colors ${
        aiDone ? "bg-emerald-50 border-emerald-300" : "bg-blue-50 border-blue-200"
      }`}>
        <p className={`text-sm ${aiDone ? "text-emerald-900 font-semibold" : "text-blue-900"}`}>
          {aiDone ? "✓ 配置が完了しました。日ごとに調整できます。" : "まずは配置マジックを実行してください。"}
        </p>
        <Button
          data-testid="goto-step3-from-month"
          onClick={onNext}
          disabled={!aiDone}
          className={`${aiDone ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-300 cursor-not-allowed"}`}
          title={aiDone ? "" : "配置マジックを起動してください"}
        >
          次へ：日を調整する <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Card>

      {/* 配置マジック演出オーバーレイ */}
      {magicStage && <MagicOverlay stage={magicStage} />}
      </>)}
    </div>
  );
}








// ========== STEP 2: 日を調整する ==========
function StepAdjustDay({ period, days, periodId, shifts, requests, staffStatusMap, assignmentOptions, selectedDate, setSelectedDate, removals, onChanged, onShiftAdded, onPrev, onNext }) {
  const isDesktop = useIsDesktop();
  // モバイル/タブレットでは Sheet で日詳細を表示
  const sheetOpen = !isDesktop && !!selectedDate;
  const closeSheet = () => setSelectedDate("");

  // 月内の有効な日付一覧 (休業日も含む。スワイプではスキップしない=全日めくり)
  const allDateList = useMemo(() => {
    if (!period) return [];
    const list = [];
    for (let d = 1; d <= days; d++) {
      list.push(`${period.year}-${pad(period.month)}-${pad(d)}`);
    }
    return list;
  }, [period, days]);
  const closedSet = useMemo(() => new Set(period?.closed_dates || []), [period]);

  const findAdjacent = (cur, dir) => {
    // dir: -1 prev, +1 next。休業日はスキップして最初の通常日へ
    const idx = allDateList.indexOf(cur);
    if (idx < 0) return null;
    let i = idx + dir;
    while (i >= 0 && i < allDateList.length) {
      if (!closedSet.has(allDateList[i])) return allDateList[i];
      i += dir;
    }
    return null;
  };
  const prevDate = selectedDate ? findAdjacent(selectedDate, -1) : null;
  const nextDate = selectedDate ? findAdjacent(selectedDate, +1) : null;
  const goPrev = () => prevDate && setSelectedDate(prevDate);
  const goNext = () => nextDate && setSelectedDate(nextDate);

  // モバイル用 (Sheetが独自の×を持つので内側は非表示)
  const dayPaneMobile = (
    <DayDetailPane
      period={period} periodId={periodId} date={selectedDate}
      shifts={shifts} requests={requests} staffStatusMap={staffStatusMap}
      assignmentOptions={assignmentOptions} removals={removals}
      onChanged={onChanged} onShiftAdded={onShiftAdded} onClose={() => setSelectedDate("")}
      hideHeaderClose
    />
  );

  // 共通の日詳細パネル要素
  const dayPane = (
    <DayDetailPane
      period={period} periodId={periodId} date={selectedDate}
      shifts={shifts} requests={requests} staffStatusMap={staffStatusMap}
      assignmentOptions={assignmentOptions} removals={removals}
      onChanged={onChanged} onShiftAdded={onShiftAdded} onClose={() => setSelectedDate("")}
    />
  );

  // --- モバイル / タブレット: 簡易カレンダー + Sheet ---
  if (!isDesktop) {
    return (
      <div className="space-y-3" data-testid="step2-adjust">
        <Card className="p-4 bg-white border border-slate-200">
          <p className="text-sm text-slate-600 mb-2">日付をタップして、その日の出勤メンバーを調整します</p>
          <MonthCalendar
            period={period} days={days} shifts={shifts} requests={requests}
            onDateClick={setSelectedDate} selectedDate={selectedDate}
            compact
          />
          <div className="flex justify-between mt-3">
            <Button variant="outline" onClick={onPrev}><ChevronLeft className="w-4 h-4 mr-1" />戻る</Button>
            <Button data-testid="goto-publish" onClick={onNext} className="bg-blue-600 hover:bg-blue-700">次へ <ChevronRight className="w-4 h-4 ml-1" /></Button>
          </div>
        </Card>

        <Sheet open={sheetOpen} onOpenChange={(v) => !v && closeSheet()}>
          <SheetContent
            side="bottom"
            className="p-0 h-[92vh] max-h-[92vh] overflow-hidden rounded-t-2xl flex flex-col"
            data-testid="day-detail-sheet"
          >
            <SwipeWrapper onSwipeLeft={goNext} onSwipeRight={goPrev}>
              {/* 前日/翌日ナビ + カレンダーへ戻る */}
              <div className="flex items-center justify-between gap-1 px-2 py-1.5 border-b border-slate-100 bg-slate-50 shrink-0">
                <Button
                  variant="outline" size="sm"
                  className="h-8 text-xs px-2.5 bg-white border-blue-200 text-blue-700 hover:bg-blue-50 font-semibold"
                  onClick={closeSheet}
                  data-testid="back-to-calendar"
                >
                  <ChevronLeft className="w-4 h-4 mr-0.5" />カレンダー
                </Button>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost" size="sm"
                    className="h-8 text-xs px-2 disabled:opacity-30"
                    onClick={goPrev} disabled={!prevDate}
                    data-testid="day-prev"
                  >
                    <ChevronLeft className="w-4 h-4 mr-0.5" />前日
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    className="h-8 text-xs px-2 disabled:opacity-30"
                    onClick={goNext} disabled={!nextDate}
                    data-testid="day-next"
                  >
                    翌日<ChevronRight className="w-4 h-4 ml-0.5" />
                  </Button>
                </div>
              </div>
              <div className="text-[10px] text-slate-400 text-center py-1 bg-slate-50 border-b border-slate-100 shrink-0">
                ← スワイプで前日／翌日に切替 →
              </div>
              <div className="flex-1 overflow-y-auto" data-testid="day-detail-scroll">
                {dayPaneMobile}
              </div>
            </SwipeWrapper>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // --- デスクトップ (>=1024px): 既存の左右レイアウト ---
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-3" data-testid="step2-adjust">
      <Card className="p-4 bg-white border border-slate-200">
        <p className="text-sm text-slate-600 mb-2">日付をクリックして、その日の出勤メンバーを調整します</p>
        <MonthCalendar period={period} days={days} shifts={shifts} requests={requests} onDateClick={setSelectedDate} selectedDate={selectedDate} />
        <div className="flex justify-between mt-3">
          <Button variant="outline" onClick={onPrev}><ChevronLeft className="w-4 h-4 mr-1" />戻る</Button>
          <Button data-testid="goto-publish" onClick={onNext} className="bg-blue-600 hover:bg-blue-700">確定する <ChevronRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </Card>

      {dayPane}
    </div>
  );
}

// スワイプ検出ラッパー: 水平移動が縦移動より大きく、閾値以上のときだけ発火

function DayDetailPane({ period, periodId, date, shifts, requests, staffStatusMap, assignmentOptions, removals, onChanged, onShiftAdded, onClose, hideHeaderClose = false }) {
  const [candidates, setCandidates] = useState(null);
  const [requestMessages, setRequestMessages] = useState([]); // この日に送った検討依頼
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null);
  const [msgBody, setMsgBody] = useState("");
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeShift, setTimeShift] = useState(null);
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [candTab, setCandTab] = useState("available"); // 'available' | 'either' | 'unavailable'
  const [confirmRemove, setConfirmRemove] = useState(null); // {shift, name}

  const loadRequestMessages = async () => {
    if (!date || !periodId) return;
    try {
      const { data } = await api.get(`/messages?related_date=${date}`);
      // この期間 + work_request のみ
      setRequestMessages(
        (data || []).filter(
          (m) => m.message_type === "work_request" && (!m.shift_period_id || m.shift_period_id === periodId),
        ),
      );
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (!date) { setCandidates(null); setRequestMessages([]); return; }
    api.get(`/shifts/day-candidates/${periodId}?date=${date}`)
      .then((r) => setCandidates(r.data.candidates || []))
      .catch((err) => toast.error(formatApiError(err)));
    loadRequestMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, periodId, shifts]);

  if (!date) {
    return (
      <Card className="p-6 bg-slate-50 border-dashed border-2 border-slate-200 flex items-center justify-center min-h-[400px]">
        <p className="text-sm text-slate-400">左のカレンダーから日付を選んでください</p>
      </Card>
    );
  }

  const dayShifts = shifts.filter((s) => s.date === date && s.status !== "cancelled" && s.status !== "draft");
  const placed = dayShifts.length;
  const reqOnDate = requests.filter((r) => r.date === date);
  const availCount = reqOnDate.filter((r) => r.request_type === "available").length;
  const eitherCount = reqOnDate.filter((r) => r.request_type === "either").length;
  const unavailCount = reqOnDate.filter((r) => r.request_type === "unavailable").length;

  const candFiltered = candidates || [];
  const candAvailable = candFiltered.filter((c) => !c.already_assigned && c.request_type === "available");
  const candEither = candFiltered.filter((c) => !c.already_assigned && c.request_type === "either");
  const candUnavailable = candFiltered.filter((c) => !c.already_assigned && c.request_type === "unavailable");
  const candNoRequest = candFiltered.filter((c) => !c.already_assigned && !c.request_type);

  // 各スタッフの「この日の希望状況・希望時間」を引けるようにする
  const reqTypeByUser = {};
  const reqTimeByUser = {};
  for (const r of reqOnDate) {
    reqTypeByUser[r.user_id] = r.request_type;
    reqTimeByUser[r.user_id] = { start: r.start_time, end: r.end_time };
  }

  const wd = new Date(date + "T00:00:00").getDay();
  const dateLabel = `${parseInt(date.split("-")[1])}月${parseInt(date.split("-")[2])}日 (${wdLabel(wd)})`;

  // ===== Actions =====
  const removeShift = (s) => {
    setConfirmRemove({ shift: s, name: nameOf(staffStatusMap, s.user_id) });
  };
  const doRemoveShift = async () => {
    const s = confirmRemove?.shift;
    if (!s) return;
    setConfirmRemove(null);
    try {
      await api.post(`/shifts/${s.id}/remove-with-reason`, { reason: "調整", reason_detail: null });
      toast.success("外しました");
      await onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setAssignment = async (s, v) => {
    try {
      await api.put(`/shifts/${s.id}`, { assignment: v || null });
      await onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addCandidate = async (c, force = false) => {
    try {
      const { data: newShift } = await api.post("/shifts", {
        shift_period_id: periodId,
        user_id: c.user_id,
        date,
        start_time: c.request_start_time || null,
        end_time: c.request_end_time || null,
        status: "temporary",
        force,
      });
      // ① 候補リストから即座に消す (already_assigned=true)
      setCandidates((prev) => (prev || []).map((x) =>
        x.user_id === c.user_id ? { ...x, already_assigned: true } : x
      ));
      // ② 親の shifts state も即更新する
      if (onShiftAdded && newShift?.id) onShiftAdded(newShift);
      toast.success(`${c.display_name || c.name} を入れました`);
      // ③ バックグラウンドでサーバ状態を同期
      onChanged();
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d && typeof d === "object" && (d.code === "limit_warning" || d.code === "user_unavailable")) {
        if (window.confirm(d.message + "\n\nそれでも入れますか？")) return addCandidate(c, true);
        return;
      }
      toast.error(formatApiError(err));
    }
  };

  const openMsg = (c) => {
    setMsgTarget(c);
    const m = parseInt(date.split("-")[1]);
    const d = parseInt(date.split("-")[2]);
    setMsgBody(
      `${m}月${d}日のシフトについて検討のお願いです。\n人が少なく困っており、もしかしたらお願いするかもしれません。出勤できそうか検討いただけませんか？\n（承諾されても、こちらで再度判断してからシフトに入れます）`,
    );
    setMsgOpen(true);
  };
  const sendMsg = async () => {
    try {
      await api.post("/messages", {
        to_user_id: msgTarget.user_id,
        related_date: date,
        message_type: "work_request",
        body: msgBody,
        shift_period_id: periodId,
      });
      toast.success("検討依頼を送りました");
      setMsgOpen(false);
      loadRequestMessages();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // 対象スタッフへの「検討依頼」ステータス取得
  // 戻り値: null (依頼なし) | { state: 'pending'|'accepted'|'rejected'|'counter', counter: {start,end}? }
  const getRequestStatus = (userId) => {
    const m = requestMessages.find((x) => x.to_user_id === userId);
    if (!m) return null;
    const a = (m.actions || [])[0];
    if (!a) return { state: "pending" };
    if (a.action === "accept") return { state: "accepted" };
    if (a.action === "reject") return { state: "rejected" };
    if (a.action === "counter_offer") return { state: "counter", counter: { start: a.counter_start_time, end: a.counter_end_time } };
    return { state: "pending" };
  };

  const openTime = (s) => {
    setTimeShift(s);
    setTimeStart(s.start_time || "");
    setTimeEnd(s.end_time || "");
    setTimeOpen(true);
  };
  const saveTime = async () => {
    try {
      await api.put(`/shifts/${timeShift.id}`, {
        start_time: timeStart || null,
        end_time: timeEnd || null,
      });
      toast.success("時間を保存しました");
      setTimeOpen(false);
      await onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // この1人を「確定」にすると配置マジック(fresh)でも崩されない
  const toggleConfirm = async (s) => {
    const next = s.status === "confirmed" ? "temporary" : "confirmed";
    try {
      await api.put(`/shifts/${s.id}`, { status: next });
      toast.success(next === "confirmed" ? "この人を確定しました（マジック再実行時も保持されます）" : "仮に戻しました");
      await onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Card className="bg-white border border-slate-200 overflow-hidden" data-testid="day-detail-pane">
      {/* Header */}
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-900">{dateLabel}</h3>
        {!hideHeaderClose && (
          <Button size="sm" variant="ghost" onClick={onClose} data-testid="close-day-pane">
            <XIcon className="w-4 h-4 mr-1" />閉じる
          </Button>
        )}
      </div>
      {/* Summary cards (mockup style) */}
      <div className="p-3 grid grid-cols-4 gap-2 border-b border-slate-200 bg-slate-50">
        <SummaryCard label="配置済み" value={placed} tone="blue" />
        <SummaryCard label="◎候補" value={availCount} tone="emerald" />
        <SummaryCard label="△候補" value={eitherCount} tone="amber" />
        <SummaryCard label="×不可" value={unavailCount} tone="rose" />
      </div>

      <div className="p-3 space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto">
        {/* 入っている人 */}
        <section>
          <h4 className="text-sm font-bold text-slate-900 mb-2">入っている人 ({placed})</h4>
          {placed === 0 ? (
            <div className="py-4 flex flex-col items-center" data-testid="empty-placed">
              <img src="/yasamaru-shy-128.png" alt="やさまる" className="w-16 h-16 opacity-80" />
              <p className="text-xs text-slate-500 mt-1">まだ誰も入っていません</p>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {dayShifts.map((s) => {
                const sm = staffStatusMap[s.user_id];
                const reqType = reqTypeByUser[s.user_id];
                const rt = reqTimeByUser[s.user_id];
                const placedTime = (s.start_time && s.end_time) ? `${s.start_time}〜${s.end_time}` : "時間未設定";
                const wantTime = (rt?.start && rt?.end) ? `${rt.start}〜${rt.end}` : null;
                const timeDiffers = wantTime && s.start_time && s.end_time && (rt.start !== s.start_time || rt.end !== s.end_time);
                const isConfirmed = s.status === "confirmed";
                return (
                  <li key={s.id} className={`border rounded p-2 ${isConfirmed ? "border-emerald-300 bg-emerald-50" : "border-slate-200"}`} data-testid={`placed-${s.id}`}>
                    {/* Row 1: name + status badges */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="font-semibold text-sm text-slate-900">{sm?.display_name || sm?.name}</span>
                      {isConfirmed && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] bg-emerald-200 text-emerald-900 rounded px-1.5 py-0.5 font-bold" title="マジック再実行で崩れません">
                          <Lock className="w-3 h-3" />確定
                        </span>
                      )}
                      <RequestTypeBadge type={reqType} />
                      <AssignmentChip value={s.assignment} />
                    </div>
                    {/* Row 1.5: capable_assignments (対応可能配置) */}
                    {sm?.capable_assignments && sm.capable_assignments.length > 0 && (
                      <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`placed-caps-${s.id}`}>
                        <span className="text-[10px] text-slate-400">可能</span>
                        {sm.capable_assignments.slice(0, 5).map((a) => (
                          <span key={a} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">{a}</span>
                        ))}
                        {sm.capable_assignments.length > 5 && <span className="text-[10px] text-slate-400">+{sm.capable_assignments.length - 5}</span>}
                      </div>
                    )}
                    {/* Row 2: time info */}
                    <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px]">
                      <span className="text-slate-700">配置 <b className="text-slate-900">{placedTime}</b></span>
                      {wantTime && (
                        <span className={timeDiffers ? "text-amber-700" : "text-slate-500"}>
                          希望 <b>{wantTime}</b>{timeDiffers && " ⚠"}
                        </span>
                      )}
                    </div>
                    {/* Row 3: actions */}
                    <div className="mt-1.5 flex gap-1 flex-wrap">
                      <Select value={s.assignment || "_none"} onValueChange={(v) => setAssignment(s, v === "_none" ? null : v)}>
                        <SelectTrigger className="h-7 text-xs w-24" data-testid={`assign-${s.id}`}><SelectValue placeholder="配置" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">未設定</SelectItem>
                          {(assignmentOptions || ASSIGNMENT_OPTIONS).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => openTime(s)} data-testid={`time-${s.id}`}>時間</Button>
                      <Button
                        size="sm" variant="outline"
                        className={`h-7 text-xs px-2 ${isConfirmed ? "text-slate-700 border-slate-300" : "text-emerald-700 border-emerald-300 bg-white hover:bg-emerald-50"}`}
                        onClick={() => toggleConfirm(s)}
                        data-testid={`confirm-${s.id}`}
                      >
                        {isConfirmed ? (<><Unlock className="w-3 h-3 mr-0.5" />仮に戻す</>) : (<><Lock className="w-3 h-3 mr-0.5" />確定</>)}
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-rose-700 border-rose-200 ml-auto" onClick={() => removeShift(s)} data-testid={`remove-${s.id}`}>
                        <Trash2 className="w-3 h-3 mr-0.5" />外す
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* 候補スタッフ (tabbed) */}
        <section>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-slate-900">候補スタッフ</h4>
            {candNoRequest.length > 0 && (
              <span className="text-[10px] text-slate-400">未提出 {candNoRequest.length}名</span>
            )}
          </div>
          <div className="flex gap-1 mb-2 border-b border-slate-200">
            {[
              { key: "available", label: "◎入れます", count: candAvailable.length, color: "emerald" },
              { key: "either", label: "△必要なら", count: candEither.length, color: "amber" },
              { key: "unavailable", label: "×入れません", count: candUnavailable.length, color: "rose" },
            ].map((t) => {
              const active = candTab === t.key;
              const colors = {
                emerald: active ? "text-emerald-700 border-emerald-500" : "text-slate-500 border-transparent hover:text-emerald-600",
                amber: active ? "text-amber-700 border-amber-500" : "text-slate-500 border-transparent hover:text-amber-600",
                rose: active ? "text-rose-700 border-rose-500" : "text-slate-500 border-transparent hover:text-rose-600",
              };
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setCandTab(t.key)}
                  data-testid={`cand-tab-${t.key}`}
                  className={`px-3 py-1.5 text-xs font-semibold border-b-2 transition-colors ${colors[t.color]}`}
                >
                  {t.label} ({t.count})
                </button>
              );
            })}
          </div>
          {(() => {
            const list = candTab === "available" ? candAvailable : candTab === "either" ? candEither : candUnavailable;
            if (list.length === 0) {
              return (
                <div className="py-3 flex flex-col items-center bg-slate-50 rounded">
                  <img src="/yasamaru-shy-128.png" alt="やさまる" className="w-12 h-12 opacity-80" />
                  <p className="text-xs text-slate-500 mt-1">該当者はいません</p>
                </div>
              );
            }
            const showActions = candTab !== "unavailable";
            return (
              <ul className="space-y-1.5">
                {list.map((c) => {
                  const wantTime = (c.request_start_time && c.request_end_time)
                    ? `${c.request_start_time}〜${c.request_end_time}`
                    : null;
                  const caps = c.capable_assignments || [];
                  const reqStatus = getRequestStatus(c.user_id);
                  return (
                    <li key={c.user_id} className={`border rounded p-2 ${candTab === "unavailable" ? "border-rose-200 bg-rose-50/50" : reqStatus?.state === "accepted" ? "border-emerald-300 bg-emerald-50/50" : "border-slate-200"}`} data-testid={`cand-${c.user_id}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0 flex-1">
                          <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          <span className="font-semibold text-sm whitespace-nowrap">{c.display_name || c.name}</span>
                          {reqStatus && (
                            <span
                              data-testid={`req-status-${c.user_id}`}
                              className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 border ${
                                reqStatus.state === "accepted"
                                  ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                                  : reqStatus.state === "rejected"
                                    ? "bg-rose-100 text-rose-700 border-rose-300"
                                    : reqStatus.state === "counter"
                                      ? "bg-sky-100 text-sky-700 border-sky-300"
                                      : "bg-amber-100 text-amber-700 border-amber-300"
                              }`}
                            >
                              {reqStatus.state === "accepted"
                                ? "✓ 検討OK"
                                : reqStatus.state === "rejected"
                                  ? "× NG"
                                  : reqStatus.state === "counter"
                                    ? `代替 ${reqStatus.counter?.start || ""}〜${reqStatus.counter?.end || ""}`
                                    : "依頼中"}
                            </span>
                          )}
                          {caps.length > 0 ? (
                            caps.slice(0, 4).map((a) => (
                              <span key={a} className="text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5">{a}</span>
                            ))
                          ) : (
                            <span className="text-[10px] text-slate-400">配置 未設定</span>
                          )}
                          {caps.length > 4 && <span className="text-[10px] text-slate-400">+{caps.length - 4}</span>}
                        </div>
                        {showActions && (
                          <div className="flex gap-1">
                            <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => addCandidate(c)} data-testid={`add-${c.user_id}`}>
                              <Plus className="w-3 h-3 mr-0.5" />入れる
                            </Button>
                            {!reqStatus && (
                              <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-800" onClick={() => openMsg(c)} data-testid={`req-${c.user_id}`}>
                                <MessageSquare className="w-3 h-3 mr-0.5" />検討依頼
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span>週 <b className="text-slate-700">{c.weekly_shift_days}/{c.weekly_max_days ?? "-"}</b></span>
                        <span>月 <b className="text-slate-700">{c.total_shift_days}/{c.monthly_desired_days ?? "-"}</b></span>
                        {wantTime ? (
                          <span>希望時間 <b className="text-slate-700">{wantTime}</b></span>
                        ) : (
                          <span className="text-slate-400">希望時間 未指定</span>
                        )}
                      </p>
                    </li>
                  );
                })}
              </ul>
            );
          })()}
        </section>
      </div>

      {/* Message dialog */}
      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>検討依頼を送る: {msgTarget?.display_name || msgTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">対象日: {date}</p>
          <Textarea rows={5} value={msgBody} onChange={(e) => setMsgBody(e.target.value)} data-testid="msg-body" />
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed">
            ※ 承諾されてもシフトには自動追加されません。<br />
            返信を確認した後、こちらの画面で「入れる」ボタンを押すとシフトに追加されます。
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMsgOpen(false)}>キャンセル</Button>
            <Button onClick={sendMsg} className="bg-blue-600 hover:bg-blue-700" data-testid="send-msg">送信</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time edit dialog */}
      <Dialog open={timeOpen} onOpenChange={setTimeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>時間を調整: {timeShift ? (staffStatusMap[timeShift.user_id]?.display_name || staffStatusMap[timeShift.user_id]?.name) : ""}</DialogTitle>
          </DialogHeader>
          {timeShift && reqTimeByUser[timeShift.user_id]?.start && (
            <p className="text-xs text-slate-600">希望時間: <b>{reqTimeByUser[timeShift.user_id].start}〜{reqTimeByUser[timeShift.user_id].end}</b></p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">開始</Label>
              <input type="time" data-testid="time-start" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">終了</Label>
              <input type="time" data-testid="time-end" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm" />
            </div>
          </div>
          {timeShift && reqTimeByUser[timeShift.user_id]?.start && (
            <Button variant="outline" size="sm" className="self-start text-xs"
              onClick={() => {
                setTimeStart(reqTimeByUser[timeShift.user_id].start || "");
                setTimeEnd(reqTimeByUser[timeShift.user_id].end || "");
              }}>
              希望時間に合わせる
            </Button>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeOpen(false)}>キャンセル</Button>
            <Button onClick={saveTime} className="bg-blue-600 hover:bg-blue-700" data-testid="save-time">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove confirm dialog (shadcn AlertDialog — iframeでも動作) */}
      <AlertDialog open={!!confirmRemove} onOpenChange={(v) => !v && setConfirmRemove(null)}>
        <AlertDialogContent data-testid="remove-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>シフトから外しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{confirmRemove?.name}</b> さんを <b>{date}</b> から外します。<br />
              ※ あとから候補スタッフに表示され、もう一度入れることもできます。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel">キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={doRemoveShift} className="bg-rose-600 hover:bg-rose-700" data-testid="remove-confirm">外す</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}






// ========== STEP 4: スタッフ別設定状況 (シンプル版: 日ごとを見るボタンのみ) ==========
function StepStaffReview({ periodId, onPrev, onNext }) {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState(null);
  const [requests, setRequests] = useState([]);
  const [shiftsLocal, setShiftsLocal] = useState([]);
  const [detailUser, setDetailUser] = useState(null);

  useEffect(() => {
    if (!periodId) return;
    api.get(`/shifts/staff-status/${periodId}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
    api.get(`/shift-periods/${periodId}`).then((r) => setPeriod(r.data)).catch(() => {});
    api.get(`/shift-requests?shift_period_id=${periodId}`).then((r) => setRequests(r.data || [])).catch(() => setRequests([]));
    api.get(`/shifts?shift_period_id=${periodId}`).then((r) => setShiftsLocal(r.data || [])).catch(() => setShiftsLocal([]));
  }, [periodId]);

  const days = period ? new Date(period.year, period.month, 0).getDate() : 0;
  const closedSet = new Set(period?.closed_dates || []);

  return (
    <div className="space-y-3" data-testid="step4-staff-review">
      <Card className="p-4 bg-white border border-slate-200">
        <h2 className="text-lg font-bold text-slate-900 mb-1">スタッフ別 設定状況</h2>
        <p className="text-xs text-slate-500">各スタッフを選んで「日ごとを見る」で、希望と配置を日別に確認できます。</p>
      </Card>

      <Card className="bg-white border border-slate-200 divide-y divide-slate-100">
        {rows.map((r) => (
          <div key={r.user_id} className="px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`staff-review-${r.user_id}`}>
            <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
              <span className="font-semibold text-sm text-slate-900">{r.display_name || r.name}</span>
              <span className="text-[11px] text-slate-500">
                <span className="text-emerald-700 font-semibold">◎{r.requested_days}</span>
                <span className="mx-1 text-amber-700">△{r.either_days}</span>
                <span className="text-rose-700">×{r.unavailable_days}</span>
                <span className="ml-3 text-blue-700">入っている <b>{r.total_shift_days}</b>日</span>
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs whitespace-nowrap"
              onClick={() => setDetailUser(r)}
              data-testid={`view-day-${r.user_id}`}
            >
              <Eye className="w-3.5 h-3.5 mr-1" />日ごとを見る
            </Button>
          </div>
        ))}
        {rows.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400">データがありません</p>
        )}
      </Card>

      <DayByDayDialog
        user={detailUser}
        period={period}
        days={days}
        closedSet={closedSet}
        requests={requests}
        shifts={shiftsLocal}
        onClose={() => setDetailUser(null)}
      />

      <Card className="p-4 bg-blue-50 border-2 border-blue-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onPrev} data-testid="back-to-adjust"><ChevronLeft className="w-4 h-4 mr-1" />戻る (日を調整)</Button>
        </div>
        <Button data-testid="goto-publish" onClick={onNext} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          次へ：確定する <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Card>
    </div>
  );
}



function StepPublish({ period, periodId, review, shifts, staffStatusMap, onPublished, onPrev, onJumpToDay }) {
  const [publishing, setPublishing] = useState(false);
  const isPublished = period?.status === "published" || period?.status === "closed";

  const publish = async () => {
    if (!window.confirm("仮シフトを全て確定し、対象月を公開します。よろしいですか？")) return;
    setPublishing(true);
    try {
      // サーバー側で「全仮シフト → confirmed」「対象月 → published」を一括処理。
      // 途中失敗による半端な状態を防ぐため、必ずこの1本のAPIで完結させる。
      await api.post(`/shift-periods/${periodId}/publish`);
      toast.success("対象月を公開しました");
      await onPublished();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPublishing(false); }
  };

  const unpublish = async () => {
    if (!window.confirm("公開を取り消します。スタッフからは見えなくなります。よろしいですか？")) return;
    try {
      await api.put(`/shift-periods/${periodId}`, { status: "adjusting" });
      await onPublished();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const w = review?.warnings || {};

  return (
    <div className="space-y-4" data-testid="step3-publish">
      <Card className="p-4 bg-white border border-slate-200">
        <h2 className="text-lg font-bold text-slate-900">確定する (全体の最終確認)</h2>
        <p className="text-xs text-slate-500">確定前チェック。クリックでその対象の詳細が見られます。</p>
      </Card>

      {!review ? (
        <Card className="p-6 text-sm text-slate-400">読み込み中...</Card>
      ) : (
        <Card className="p-4 bg-white border border-slate-200">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <CheckCard title="希望より少ない人" items={w.under_desired} tone="yellow" />
            <CheckCard title="希望より多い人" items={w.over_desired} tone="purple" />
            <CheckCard title="上限超過 (希望超え)" items={w.over_max} tone="rose" />
            <CheckCard title="連勤注意" items={[]} tone="orange" disabled />
            <CheckCard title="配置未設定" count={w.unassigned_count} tone="slate" />
            <CheckCard title="出勤不可日に配置" items={w.unavailable_assigned} tone="rose" onDateClick={onJumpToDay} />
          </div>
        </Card>
      )}

      <Card className={`p-5 border-2 ${isPublished ? "bg-emerald-50 border-emerald-300" : "bg-blue-50 border-blue-300"}`}>
        {isPublished ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="w-5 h-5" />
              <p className="font-bold">この対象月は公開中です。</p>
            </div>
            <Button data-testid="unpublish" variant="outline" onClick={unpublish}>公開を取り消す</Button>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-blue-900">確定すると、スタッフにシフトが公開されます。</p>
            <Button data-testid="publish-button" onClick={publish} disabled={publishing} className="bg-blue-600 hover:bg-blue-700 text-white h-12 text-base">
              <Sparkles className="w-5 h-5 mr-2" />{publishing ? "公開中..." : "確定する (公開する)"}
            </Button>
          </div>
        )}
      </Card>

      <div className="flex items-center">
        <Button variant="outline" onClick={onPrev}><ChevronLeft className="w-4 h-4 mr-1" />一時保存に戻る</Button>
      </div>
    </div>
  );
}




